import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LoadingState } from "@shared/common/components/State/LoadingState";
import { InstanceList } from "./InstanceList";
import {
  ImportKapiInstanceListRequest,
  ImportKapiInstanceByNameRequest,
} from "@opstel/gen/knop/ui/v1/api/instance_loading_pb";
import { MessageBar } from "@shared/EDS/components/MessageBar";
import { useCallback } from "react";
import { useSessionStorage } from "@uidotdev/usehooks";
import { useKnopIsLoading } from "@shared/common/hooks/useKnopIsLoading";
import { messageToast } from "@shared/EDS/components/MessageToast/messageToastFunctions.tsx";
import { CONFIRM_LOAD_DIALOG } from "@opstel/dialogs/InstanceDialog/ConfirmLoadPendingInstanceDialog.tsx";
import { KapiNotification } from "@opstel/query/models/kapiNotification.ts";
import { useDialogStore } from "@shared/common/contexts/dialog/useDialogStore.ts";
import { useCurrentInstanceId } from "@shared/common/contexts/instanceMetadata/useInstanceId";
import { useKnopClient } from "@opstel/contexts/knopClient/useKnopClient";
import { usePendingInstanceData } from "./usePendingInstanceData";
import { LocationMetadata } from "@opstel/query/models/locationMetadata";

const REFRESH_INTERVAL_IN_MS = 30_000;

export interface PendingInstanceData {
  notifications: KapiNotification[];
  instanceId: string;
}
interface InstanceListWrapperProps {
  readonly onBack: () => void;
  readonly onClose: () => void;
}

export function InstanceListWrapper({
  onBack,
  onClose,
}: InstanceListWrapperProps): React.JSX.Element {
  const { knopClient: kapiClient } = useKnopClient();
  const openDialog = useDialogStore((state) => state.openDialog);

  const [location, setLocation] = useSessionStorage<string | undefined>(
    "locationSelected",
    undefined,
  );
  const setPendingInstanceData = usePendingInstanceData((state) => state.setData);
  const setLoading = useKnopIsLoading((state) => state.setLoading);
  const setReady = useKnopIsLoading((state) => state.setReady);
  const queryClient = useQueryClient();
  const { setCurrentInstanceId } = useCurrentInstanceId();

  const locationListQuery = useQuery({
    queryKey: ["location-list"],
    queryFn: async (): Promise<LocationMetadata[]> => {
      return (await kapiClient.getLocationList()).toSorted((a, b) => a.name.localeCompare(b.name));
    },
  });
  const instanceListQuery = useQuery({
    queryKey: ["instance-list", location],
    refetchInterval: REFRESH_INTERVAL_IN_MS,
    queryFn: async () => {
      if (!location) {
        return null;
      }
      return await kapiClient.getKapiInstanceList(
        new ImportKapiInstanceListRequest({ locationCode: location }),
      );
    },
  });

  const pickInstance = useMutation({
    mutationFn: (instanceName: string) =>
      kapiClient.importKapiInstanceByName(new ImportKapiInstanceByNameRequest({ instanceName })),
    onMutate: () => {
      onClose();
      setLoading();
    },
    onSettled: () => setReady(),
    onSuccess: (data) => {
      if (data.notifications.length === 0) {
        setCurrentInstanceId(data.instanceId);
      } else {
        setPendingInstanceData({ notifications: data.notifications, instanceId: data.instanceId });
        openDialog(CONFIRM_LOAD_DIALOG);
      }
    },
    onError: (err) => {
      console.error(err);
      messageToast({
        message: `Het laden van het scenario is mislukt.`,
        type: "error",
      });
    },
  });

  type HandleModelValueChangeEvent = CustomEvent<{
    isTriggeredByUser: boolean;
    formPath: { name: string; modelValue: string }[];
  }>;
  const handleSelectLocation = useCallback(
    (event: HandleModelValueChangeEvent) => {
      if (event.detail.isTriggeredByUser) {
        const location = event.detail.formPath[0].modelValue;
        if (location === "") {
          setLocation(undefined);
        } else {
          setLocation(location);
        }
        queryClient.invalidateQueries(instanceListQuery).catch(console.error);
      }
    },
    [setLocation, queryClient, instanceListQuery],
  );

  if (locationListQuery.isLoading) {
    return <LoadingState fullPage={false} />;
  }
  if (locationListQuery.isError) {
    console.error(locationListQuery.error);
  }
  if (instanceListQuery.isError) {
    console.error(instanceListQuery.error);
  }
  if (pickInstance.isError) {
    console.error(pickInstance.failureReason);
  }

  const anyQueryHasError =
    locationListQuery.isError || instanceListQuery.isError || pickInstance.isError;
  return (
    <>
      {/* Replace with tractie-section-header when available */}
      <div slot="start" className="flex items-center gap-s">
        <tractie-button icon-only level="tertiary" onClick={onBack} aria-label="Back button">
          <tractie-icon name="arrow-left" slot="leading" />
        </tractie-button>
        <h4 className="m-0">Importeren plan</h4>
      </div>
      <div className="min-h-100">
        {anyQueryHasError && (
          <div className="mx-[-20px] pb-m">
            {locationListQuery.isError && (
              <MessageBar messageBarType="error">
                Het laden van de lijst met knopen is mislukt
              </MessageBar>
            )}
            {instanceListQuery.isError && (
              <MessageBar messageBarType="error">
                Het laden van de lijst instanties is mislukt
              </MessageBar>
            )}
            {pickInstance.isError && (
              <MessageBar messageBarType="error">Het laden van de instantie is mislukt</MessageBar>
            )}
          </div>
        )}
        <div className="w-60">
          <tractie-select
            modelValue={location}
            label="Kies knoop"
            name="startDate"
            onmodel-value-changed={handleSelectLocation}
          >
            <select slot="input">
              <option value="">Maak een keuze</option>
              {locationListQuery.data?.map(({ code, name }) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </select>
          </tractie-select>
        </div>
        <InstanceList
          instances={instanceListQuery.data ?? null}
          openInstance={pickInstance.mutate}
          isLoading={instanceListQuery.isLoading}
        />
      </div>
    </>
  );
}
